import { JACKPOT_CONTRACT_ADDRESS, JACKPOT_FEE, SHU_DENOM } from '@/constants/constants'
import { SOUNDS, useAudio } from '@/context/AudioProvider'
import { GameStage, useJackpot } from '@/context/JackpotProvider'
import { useSession } from '@/context/SessionProvider'
import { Box, Button, Flex, Input, SimpleGrid, Text } from '@chakra-ui/react'
import { BigNumberInBase } from '@injectivelabs/utils'
import { useCallback, useEffect, useState } from 'react'
import { useForm, Controller, UseFormSetValue, UseFormGetValues } from 'react-hook-form'
import { useTranslation } from 'next-i18next/pages'
import FlashCircleIcon from '@/public/assets/icons/flash-circle.svg'
import { computed, reaction } from 'mobx'
import { Observer } from 'mobx-react'
import { StatsRow } from '@/components/atoms/StatsRow/StatsRow'
import { RulesButton } from '@/components/atoms/RulesButton/RulesButton'
import { GameRules } from 'types/types'
import { BigNumber } from '@injectivelabs/utils'
import posthog from 'posthog-js'
import { useAppToast } from '@/hooks/useAppToast'
import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { useBalanceStore } from '@/context/UserBalance'
import { placeBetTransaction } from '@/services/GameBroadcaster'
import { GameInfo } from '@/components/atoms/GameInfo/GameInfo'
import { TransactionException } from '@injectivelabs/exceptions'

interface IFormInput {
  amount: string
}

export const JackpotForm = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const jackpot = useJackpot()
  const [estimateChance, setEstimateChance] = useState('0')
  const audio = useAudio()
  const { account, signIn } = useSession()

  const { onSuccess, onError, onTransactionError } = useAppToast()

  const { control, setValue, reset, handleSubmit, getValues, watch } = useForm<IFormInput>({
    defaultValues: {
      amount: '0',
    },
  })

  const userBet = computed(
    () => jackpot.state.players.find((player) => player.address === account?.injectiveAddress)?.amount || '0'
  )
  const gamePool = computed(() => jackpot.state.pool)

  useEffect(() => {
    let amount = parseFormAmount(getValues('amount'))
    if (Number(amount) >= 100 - Number(userBet.get()) / 10 ** 6) {
      let value = BigNumber.min(
        new BigNumber(100).minus(new BigNumber(userBet.get()).div(new BigNumber(10 ** 6))),
        new BigNumber(amount)
      ).toString()
      setValue('amount', value)
      amount = parseFormAmount(value)
    }

    setEstimateChance(calculateEstimateChances(gamePool.get(), amount, userBet.get()))
  }, [watch('amount')])

  reaction(
    () => jackpot.state.pool,
    () => {
      const amount = parseFormAmount(getValues('amount'))
      setEstimateChance(calculateEstimateChances(gamePool.get(), amount, userBet.get()))
      reset({
        amount: '0',
      })
    }
  )

  const clickForm = useCallback(
    async (data: IFormInput) => {
      if (!account) {
        signIn()
        return
      }

      if (!data.amount) {
        return
      }

      setIsLoading(true)
      if (new BigNumber(jackpot.state.pool).gte(new BigNumber(2_000 * 10 ** 6))) {
        onError({
          custom: { title: t('Jackpot.form.error.max-pool-reached') },
        })
        setIsLoading(false)
        return
      }

      await placeBetTransaction({
        contract_addr: JACKPOT_CONTRACT_ADDRESS,
        funds: {
          denom: SHU_DENOM,
          amount: data.amount,
        },
        msg: {},
      })
        .then(() => {
          audio.play(SOUNDS.PLAYER_BET)
          onSuccess()
          posthog.capture('user_placed_bet', { game: 'jackpot' })
        })
        .catch((e) => {
          onTransactionError(e)
          audio.play(SOUNDS.PLAYER_BET_ERROR)
          posthog.capture('user_placed_bet_error', { game: 'jackpot' })
        })

      setIsLoading(false)
    },
    [account, reset]
  )

  return (
    <Box h={'full'} bg={'ninja.600'} py={5} px={4} rounded={'xl'}>
      <Flex justifyContent={'space-between'} mb={2}>
        <FormTitle />
        <RulesButton game={GameRules.JACKPOT} styles={{ px: 3, fontSize: 'xs' }} />
      </Flex>
      <UserBalance />
      <Controller
        control={control}
        name="amount"
        render={({ field: { onChange, value } }) => <FormInput onChange={onChange} value={value} />}
      />
      <FormControlButtons setValue={setValue} getValues={getValues} />
      <UserEstimateChances estimateChance={estimateChance} />
      <PlaceBetButton formAmount={watch('amount')} callback={handleSubmit(clickForm)} isLoading={isLoading} />
      <Box mt={2}>
        <GameInfo force={true} rtp="95%" winningFee="5%" />
      </Box>
    </Box>
  )
}

const parseFormAmount = (value: string) => {
  return value === '' || value === '0.' ? '0' : value
}

const FormInput = ({ onChange, value }: { onChange: (...event: any[]) => void; value: string }) => {
  const balance = useBalanceStore((state) => state.viewBalance)
  const { t } = useTranslation()

  const isOverBalance = parseFloat(value) > balance[SHU_DENOM].humanNumber

  return (
    <Box
      px={4}
      py={3}
      bg={'ninja.500'}
      border={isOverBalance ? '1px solid red' : '1px solid transparent'}
      rounded={'lg'}
      mb={1.5}
    >
      <Text color={'ninja.200'} fontWeight={600} fontSize={'xs'}>
        {t('Jackpot.form.amount.label')}
      </Text>
      <Flex alignItems={'center'} gap={1}>
        <Input
          onChange={(e) => {
            const raw = Number(e.target.value).toFixed(0)
            const parsed = amountParser(raw)
            if (parsed !== null) onChange(parsed)
          }}
          value={value}
          h={'24px'}
          outline={'none'}
          border={'none'}
          _focusVisible={{ outline: 'none', border: 'none' }}
          _active={{ outline: 'none', border: 'none' }}
          p={0}
          color={'brand.white'}
          _placeholder={{ color: 'ninja.300' }}
          fontWeight={700}
          fontSize={'sm'}
          placeholder={t('common.enter-amount')}
        />
      </Flex>
    </Box>
  )
}

const amountParser = (amount: string) => {
  if (isNaN(Number(amount)) || Number(amount) < 0) {
    return null
  }

  const regex = new RegExp(`^\\d+(\\.\\d{0,${2}})?$`, 'g')

  if (amount != '' && !regex.test(amount)) {
    return Number(amount)
  }

  return amount
}

const UserBalance = () => {
  const balance = useBalanceStore((state) => state.viewBalance)
  const { t } = useTranslation()

  return (
    <Flex color={'ninja.300'} fontWeight={700} gap={1} mb={1.5} fontSize={'sm'}>
      {t('common.balance')}:{' '}
      <FiatOrCryptoAmount
        value={balance[SHU_DENOM].humanFormat || '0.00'}
        denom={SHU_DENOM}
        wrapperStyles={{ fontSize: 'sm', color: 'white' }}
        iconStyles={{ display: 'none' }}
      />
    </Flex>
  )
}

const FormTitle = () => {
  const { t } = useTranslation()

  return (
    <Flex gap={2} alignItems={'center'}>
      <FlashCircleIcon />
      <Text fontSize={'xl'} fontWeight={700}>
        {t('common.your-bet')}
      </Text>
    </Flex>
  )
}

const FormControlButtons = ({
  setValue,
  getValues,
}: {
  setValue: UseFormSetValue<IFormInput>
  getValues: UseFormGetValues<IFormInput>
}) => {
  const { t } = useTranslation()
  const balance = useBalanceStore((state) => state.viewBalance)

  const handleAddAmount = (amount: number) => {
    setValue('amount', BigNumber.min(new BigNumber(100), new BigNumber(amount)).toString())
  }

  const handleAllIn = () => {
    const userBalance = balance[SHU_DENOM].humanNumber
    if (!isNaN(userBalance)) {
      setValue('amount', BigNumber.min(new BigNumber(100), new BigNumber(userBalance)).toString())
    } else {
      setValue('amount', '0')
    }
  }

  const handleMultiplyAmount = (multiplier: number) => {
    const currentAmount = Number(getValues('amount'))
    const value = isNaN(currentAmount) ? 0 : currentAmount
    setValue('amount', BigNumber.min(new BigNumber(100), new BigNumber(value).multipliedBy(multiplier)).toString())
  }

  const shuAmounts = [1, 10, 100]

  return (
    <SimpleGrid mb={3} columns={5} gap={1}>
      {shuAmounts.map((amount) => {
        return (
          <Button key={amount} onClick={() => handleAddAmount(amount)} variant={'outline'} size={'sm'}>
            {amount}
          </Button>
        )
      })}
      <Button onClick={() => handleMultiplyAmount(2)} variant={'outline'} size={'sm'} textTransform={'uppercase'}>
        x2
      </Button>
      <Button onClick={() => handleAllIn()} variant={'outline'} size={'sm'} textTransform={'uppercase'}>
        {t('common.all')}
      </Button>
    </SimpleGrid>
  )
}

const UserEstimateChances = ({ estimateChance }: { estimateChance: string }) => {
  const { t } = useTranslation()
  return <StatsRow name={t('jackpot.estimated-chances')} value={<Text fontSize={'md'}>{estimateChance + '%'}</Text>} />
}

const PlaceBetButton = ({ formAmount, isLoading, callback }: any) => {
  const jackpot = useJackpot()
  const { account } = useSession()
  const balance = useBalanceStore((state) => state.viewBalance)
  const { t } = useTranslation()

  const isAllowedToBet = () => {
    if ([GameStage.DRAWING, GameStage.FINISHED].includes(jackpot.state.state)) {
      return false
    }

    const amount = Number(formAmount)
    return (amount > 0 && amount <= balance[SHU_DENOM].humanNumber) || !account
  }

  useEffect(() => {
    if ([GameStage.DRAWING, GameStage.FINISHED].includes(jackpot.state.state)) {
      useBalanceStore.getState().lockBalance()
    }

    return () => {
      useBalanceStore.getState().unLockBalance(true)
    }
  }, [jackpot.state.state])

  return (
    <Observer>
      {() => (
        <Button isDisabled={!isAllowedToBet()} isLoading={isLoading} onClick={callback} size={'lg'} w={'full'}>
          {[GameStage.DRAWING, GameStage.FINISHED].includes(jackpot.state.state)
            ? t('Jackpot.form.button.selecting-winner')
            : t('Jackpot.form.button.place-bet')}
        </Button>
      )}
    </Observer>
  )
}

const calculateEstimateChances = (gamePool: string, amount: string, userBet: string) => {
  if (amount === '0' && userBet === '0') {
    return '0'
  }

  if (gamePool !== '0') {
    const est = new BigNumber(userBet).plus(new BigNumberInBase(amount).toWei(6))

    const estChance = Math.min(
      est.div(new BigNumber(gamePool).minus(userBet).plus(est)).multipliedBy(100).toNumber(),
      100
    )
    return estChance.toFixed(2)
  }

  if (gamePool === '0') {
    return '100'
  }

  return '0'
}
